import { Button } from "@datapay-by-datacom/design-system";
import { MAX_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../../config";
import s from "./Pagination.module.css";

interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total number of pages. */
  pageCount: number;
  /** Total number of items across all pages. */
  total: number;
  /** Items per page. */
  pageSize: number;
  /** Called with the new 1-based page number. */
  onChange: (page: number) => void;
  /**
   * When provided, renders a "Rows per page" selector. Options are filtered to
   * `<= MAX_PAGE_SIZE`. Called with the newly selected page size.
   */
  onPageSizeChange?: (pageSize: number) => void;
  /** Selectable page sizes; defaults to `PAGE_SIZE_OPTIONS`. */
  pageSizeOptions?: readonly number[];
}

/**
 * Reusable pager for the wizard's row-level tables. Renders a "showing X-Y of Z"
 * summary, an optional rows-per-page selector, and Previous/Next controls.
 */
export function Pagination({
  page,
  pageCount,
  total,
  pageSize,
  onChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: PaginationProps) {
  const start = (page - 1) * pageSize;
  const from = total === 0 ? 0 : start + 1;
  const to = Math.min(start + pageSize, total);
  const options = pageSizeOptions.filter((o) => o <= MAX_PAGE_SIZE);

  return (
    <div className={s.pagination} role="navigation" aria-label="Pagination">
      <div className={s.left}>
        <span className={s.info}>
          Showing {from}-{to} of {total}
        </span>
        {onPageSizeChange && (
          <label className={s.sizeLabel}>
            Rows per page
            <select
              className={s.sizeSelect}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label="Rows per page"
            >
              {options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className={s.controls}>
        <Button
          variant="secondary"
          isDisabled={page <= 1}
          onPress={() => onChange(page - 1)}
        >
          Previous
        </Button>
        <span className={s.pageInfo} role="status" aria-live="polite">
          Page {page} of {pageCount}
        </span>
        <Button
          variant="secondary"
          isDisabled={page >= pageCount}
          onPress={() => onChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
